import { PROMOTION_ENDPOINTS } from "../constants/promotionEndpoints";

export const createPromotionService = (httpClient) => {
  return {
    // 1. List Promotions (Customer / General View)
    listGeneral: async ({ page_number = 1, page_size = 12, order_by = "DESC" } = {}) => {
      try {
        const response = await httpClient.post(PROMOTION_ENDPOINTS.listGeneral, {
          page_number,
          page_size,
          order_by,
        });
        if (response.data) return response.data;
      } catch (err) {
        try {
          const fallback = await httpClient.post(PROMOTION_ENDPOINTS.listPreferred, {
            type: "",
            state: "",
            city: "",
            keyboard_search: "",
            page_number,
            page_size,
            order_by,
            is_partner: false,
          });
          return fallback.data || { promotions: [] };
        } catch {
          return { promotions: [] };
        }
      }
      return { promotions: [] };
    },

    // 2. List Promotions with Filters / Preferred (Partner View)
    listPreferred: async ({
      type = "",
      state = "",
      city = "",
      keyboard_search = "",
      page_number = 1,
      page_size = 12,
      order_by = "DESC",
      is_partner = true,
    } = {}) => {
      try {
        const response = await httpClient.post(PROMOTION_ENDPOINTS.listPreferred, {
          type,
          state,
          city,
          keyboard_search,
          page_number,
          page_size,
          order_by,
          is_partner,
        });
        if (response.data) return response.data;
      } catch (err) {
        try {
          const fallback = await httpClient.post(PROMOTION_ENDPOINTS.listGeneral, {
            page_number,
            page_size,
            order_by,
          });
          return fallback.data || { promotions: [] };
        } catch {
          return { promotions: [] };
        }
      }
      return { promotions: [] };
    },

    // 3. List My / Specific Partner's Promotions
    listMyPreferred: async ({
      user_id,
      page_number = 1,
      page_size = 12,
      order_by = "DESC",
    } = {}) => {
      try {
        const response = await httpClient.post(PROMOTION_ENDPOINTS.listMyPreferred, {
          user_id,
          page_number,
          page_size,
          order_by,
        });
        return response.data;
      } catch {
        try {
          const fallback = await httpClient.post(PROMOTION_ENDPOINTS.listGeneral, {
            page_number,
            page_size,
            order_by,
          });
          return fallback.data || { promotions: [] };
        } catch {
          return { promotions: [] };
        }
      }
    },

    // 4. Get Promotion Details by ID
    getDetails: async (id) => {
      const response = await httpClient.get(PROMOTION_ENDPOINTS.details(id));
      return response.data;
    },

    // 5. Add / Create Promotion
    create: async (payload) => {
      const response = await httpClient.post(PROMOTION_ENDPOINTS.create, payload);
      return response.data;
    },

    // 6. Delete Promotion
    remove: async (id) => {
      const response = await httpClient.delete(PROMOTION_ENDPOINTS.remove(id));
      return response.data;
    },

    // 7. Upload Promotion Media (Images / Videos)
    uploadMedia: async (file) => {
      const formData = new FormData();
      formData.append("files", file);
      const response = await httpClient.post(PROMOTION_ENDPOINTS.uploadMedia, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    },

    // 8. Upload Promotion Documents / Attachments
    uploadAttachments: async (file) => {
      const formData = new FormData();
      formData.append("files", file);
      const response = await httpClient.post(PROMOTION_ENDPOINTS.uploadAttachments, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    },

    // 9. AI Generate Promotion Description
    generateAiDescription: async (input) => {
      const response = await httpClient.post(PROMOTION_ENDPOINTS.aiGenerateDescription, {
        input,
      });
      return response.data;
    },

    // 10. Get Comments on a Promotion
    getComments: async (
      userId,
      promotionId,
      { page_number = 1, page_size = 200, order_by = "DESC" } = {}
    ) => {
      try {
        const response = await httpClient.post(
          PROMOTION_ENDPOINTS.comments(userId, promotionId),
          {
            page_number,
            page_size,
            order_by,
          }
        );
        return response.data;
      } catch {
        return { comments: [] };
      }
    },

    // 11. Add Comment / Reply to a Promotion
    addComment: async ({ comment, userId, promotionId, parentId = null }) => {
      const payload = {
        comment,
        user: {
          id: userId,
        },
        desired_request: {
          id: promotionId,
        },
      };

      if (parentId) {
        payload.parent_id = parentId;
      }

      const response = await httpClient.post(PROMOTION_ENDPOINTS.addComment, payload);
      return response.data;
    },

    // 12. Edit Promotion Comment
    editComment: async ({ id, comment }) => {
      const response = await httpClient.put(PROMOTION_ENDPOINTS.editComment, {
        id,
        comment,
      });
      return response.data;
    },

    // 13. Delete Promotion Comment
    deleteComment: async (commentId) => {
      const response = await httpClient.delete(
        PROMOTION_ENDPOINTS.deleteComment(commentId)
      );
      return response.data;
    },

    // Form Dropdown Helpers
    fetchStates: async (countryId = 1) => {
      try {
        const response = await httpClient.post(
          PROMOTION_ENDPOINTS.states(countryId),
          {
            page_number: 1,
            page_size: 100,
            order_by: "ASC",
          }
        );
        return response.data?.states || [];
      } catch {
        return [];
      }
    },

    fetchCities: async (stateId) => {
      try {
        const response = await httpClient.post(
          PROMOTION_ENDPOINTS.cities(stateId),
          {
            page_number: 1,
            page_size: 200,
            order_by: "ASC",
          }
        );
        return response.data?.cities || [];
      } catch {
        return [];
      }
    },

    fetchPropertyTypes: async () => {
      try {
        const response = await httpClient.get(PROMOTION_ENDPOINTS.propertyTypes);
        return response.data?.propertyTypes || [];
      } catch {
        return [];
      }
    },
  };
};
